/**
 * Måneder og datoer for Spillesteder, som ren tekst (`YYYY-MM`, `YYYY-MM-DD`).
 *
 * Kalenderen utenfra er en DATOkalender — en sal er ledig eller opptatt en dag, ikke et klokkeslett —
 * så alt her regnes i UTC på datoer uten tid. En `Date` i lokal tid ville flyttet 1. mars til 28.
 * februar for den som sitter vest for Greenwich, og det er den eneste feilen denne fila finnes for.
 */
import type { Locale } from '@tvitvi/shared';

import { intlTag } from '@/lib/format';

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Måneden i dag, i nettleserens egen kalender. */
export function currentMonth(today: Date = new Date()): string {
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
}

export function isMonthKey(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/u.test(value);
}

export function isDateKey(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/u.test(value);
}

export function shiftMonth(month: string, delta: number): string {
  const [year = 1970, mon = 1] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, mon - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
}

/** Hver dato i måneden, i rekkefølge. */
export function daysOfMonth(month: string): string[] {
  const [year = 1970, mon = 1] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  return Array.from({ length: last }, (_, index) => `${month}-${pad(index + 1)}`);
}

/** ISO-ukedag, 1 = mandag … 7 = søndag. */
export function isoWeekday(date: string): number {
  const [year = 1970, mon = 1, day = 1] = date.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, mon - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

export interface CalendarCell {
  date: string;
  inMonth: boolean;
}

/**
 * Rutenettet for en måned, mandag først, hele uker. Dagene utenfor måneden står med, men dempet —
 * som i mockupen — så ukene alltid har sju ruter.
 */
export function monthCells(month: string): CalendarCell[] {
  const days = daysOfMonth(month);
  const first = days[0] ?? `${month}-01`;
  const lead = isoWeekday(first) - 1;
  const cells: CalendarCell[] = [];
  const previous = daysOfMonth(shiftMonth(month, -1));
  for (let index = lead; index > 0; index -= 1) {
    const date = previous[previous.length - index];
    if (date !== undefined) cells.push({ date, inMonth: false });
  }
  for (const date of days) cells.push({ date, inMonth: true });
  const next = daysOfMonth(shiftMonth(month, 1));
  let index = 0;
  while (cells.length % 7 !== 0) {
    const date = next[index];
    if (date === undefined) break;
    cells.push({ date, inMonth: false });
    index += 1;
  }
  return cells;
}

/** «mars 2027» / «March 2027». */
export function monthLabel(month: string, locale: Locale): string {
  const [year = 1970, mon = 1] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(intlTag(locale), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, mon - 1, 1)));
}

/** «fre. 12. mars» — en dato uten klokkeslett, i UTC så den ikke flytter seg. */
export function dayLabel(date: string, locale: Locale): string {
  const [year = 1970, mon = 1, day = 1] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(intlTag(locale), {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, mon - 1, day)));
}

/** De sju ukedagene, mandag først, forkortet («man.», «Mon»). */
export function weekdayNames(locale: Locale): string[] {
  const format = new Intl.DateTimeFormat(intlTag(locale), { weekday: 'short', timeZone: 'UTC' });
  /* 2024-01-01 var en mandag. */
  return Array.from({ length: 7 }, (_, index) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + index))),
  );
}
